import HttpOperationError from 'es/app/business-logic/HttpOperationError';
import { GlobalStores } from 'es/app/store/GlobalStore';
import Guid, { newGuid } from 'es/core/types/Guid';

interface IErrorMessage {
    content: string;
}

export enum ErrorSeverity {
    Error = 'ERROR',
    Warning = 'WARNING',
}

export default class ApplicationError {
    public readonly id: Guid;
    public readonly raisedAtMillis: number;

    public count = 1;

    constructor(
        public readonly message: string | unknown,
        public readonly details?: string | null,
        public readonly source?: string,
        public readonly severity = ErrorSeverity.Error
    ) {
        this.id = newGuid();
        this.raisedAtMillis = new Date().getTime();
    }

    public equals(other: ApplicationError) {
        if (!other) return false;

        return (
            this.message === other.message &&
            this.details === other.details &&
            this.source === other.source &&
            this.severity === other.severity
        );
    }

    public static raiseError(
        message: string | unknown,
        details?: string | string[] | HttpOperationError | Error | IErrorMessage[] | null,
        source?: string,
        severity = ErrorSeverity.Error
    ) {
        const error = new ApplicationError(message, this.getDetails(details), source, severity);
        GlobalStores.appStore.raiseError(error);
    }

    private static getDetails(
        details?: string | string[] | HttpOperationError | Error | IErrorMessage[] | null
    ): string | null {
        if (!details) return null;
        if (typeof details === 'string') return details;

        if (Array.isArray(details))
            return (details as [])
                .map(message => {
                    const { content } = message;
                    return content || message;
                })
                .join('\n');

        // Cater for an HttpOperationError being passed as the details object
        if ((details as HttpOperationError).details)
            return ApplicationError.getDetails((details as HttpOperationError).details);

        // Cater for an Error or something that looks like one
        if (details.stack) return details.stack;
        if (details.message) return details.message;

        return details.toString();
    }
}
